// src/components/shell/Sidebar/SidebarRailButton.tsx
// Shared visual for any rail item with an icon + label.
// Used by the mode list, global items, and popover triggers so they line up.
import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";

export type SidebarRailButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  expanded: boolean;
  glyph: ReactNode;
  label: string;
  active?: boolean;
  /** Tailwind classes to color the glyph chip distinctly (e.g. amber for impersonation). */
  glyphChipClassName?: string;
  /** Inline styles for the glyph chip (e.g. Firefly intent glow). */
  glyphChipStyle?: CSSProperties;
};

export const SidebarRailButton = forwardRef<HTMLButtonElement, SidebarRailButtonProps>(
  function SidebarRailButton(
    {
      expanded,
      glyph,
      label,
      active = false,
      glyphChipClassName,
      glyphChipStyle,
      className = "",
      type = "button",
      ...rest
    },
    ref
  ) {
    const chipBase =
      "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold";
    const chipColor =
      glyphChipClassName ??
      (glyphChipStyle
        ? ""
        : active
          ? "bg-indigo-600 text-white"
          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300");
    const row =
      "w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm transition-colors " +
      (active
        ? "bg-indigo-50 text-indigo-900 font-semibold dark:bg-indigo-950/40 dark:text-indigo-100"
        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800/70");

    return (
      <button
        ref={ref}
        type={type}
        className={`${row} ${className}`.trim()}
        title={expanded ? undefined : label}
        {...rest}
      >
        <span aria-hidden className={`${chipBase} ${chipColor}`.trim()} style={glyphChipStyle}>
          {glyph}
        </span>
        {expanded ? <span className="truncate">{label}</span> : null}
      </button>
    );
  }
);
